import { useId, useState } from 'react'
import type { SubmitEvent } from 'react'

import type { EditableFieldDescription, EditFieldValue } from '@fit-editor/fit-core'

import type { FieldCellContext } from '@/components/data/field-cell-renderer'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog'
import { useUnits } from '@/hooks/use-units'
import {
  initialDraft,
  messageInstanceLabel,
  parseFieldDraft,
  RAW_ENUM_CHOICE,
  stripEditFieldErrorPrefix,
  utf8ByteLength,
} from '@/lib/field-edit'
import type { FieldEditDraft } from '@/lib/field-edit'
import { EMPTY_VALUE, formatDecodedValue, formatFriendlyValue } from '@/lib/fit-data-format'
import { dateToInputValue } from '@/lib/time-input'
import { useActivityStore } from '@/store/activity-store'

export interface FieldEditDialogProps {
  context: FieldCellContext
  /** Must be an editable description (`describeFieldEdit(...).editable`). */
  description: EditableFieldDescription
  /** `messagesKey` of `context`, narrowed to defined (editable cells always have one). */
  messagesKey: string
  onClose: () => void
}

/** Profile units whose value reads better converted (km/mi, h:mm:ss, degrees) -- shown as a hint next to the native-unit input. */
const FRIENDLY_HINT_UNITS = new Set(['m', 'm/s', 's', 'semicircles'])

const INPUT_CLASS =
  'border-input bg-background text-foreground focus-visible:ring-ring w-full rounded-md border px-2 py-1 text-sm outline-none focus-visible:ring-2 aria-[invalid=true]:border-destructive'

/**
 * Edits one field of one message instance via fit-core's `editField`
 * operation (one undo step). Mounted only while open, so the form is
 * prefilled from the field's value at the time the editor opened.
 *
 * Values are entered in the FIT field's own units (shown next to the
 * input) -- exactly what `editField` stores -- with a converted hint in the
 * app's display units where that helps. Input is validated against
 * fit-core's `describeEditableField` before anything is applied; if
 * fit-core still rejects the edit (instance-specific rules), the editor
 * stays open with the reason and nothing is committed.
 */
export function FieldEditDialog({
  context,
  description,
  messagesKey,
  onClose,
}: FieldEditDialogProps) {
  const applyOperationToSelected = useActivityStore((s) => s.applyOperationToSelected)
  const timeMode = useActivityStore((s) => s.ui.timeDisplayMode)
  const unitSystem = useUnits()
  const [draft, setDraft] = useState<FieldEditDraft>(() =>
    initialDraft(description, context.value, timeMode),
  )
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState(false)

  const inputId = useId()
  const rawInputId = useId()
  const hintId = useId()
  const errorId = useId()
  const descriptionId = useId()

  const { fieldId, messageIndex, value } = context
  const instanceLabel = messageInstanceLabel(messagesKey, messageIndex)
  const isSet = value != null
  const units = description.units
  const isBool = description.kind === 'number' && context.meta?.type === 'bool'

  const update = (patch: Partial<FieldEditDraft>) => {
    setDraft((d) => ({ ...d, ...patch }))
    setError(null)
  }

  const commit = async (newValue: EditFieldValue) => {
    setPending(true)
    setError(null)
    let failure: string | null = null
    const warnings = await applyOperationToSelected(
      { type: 'editField', messagesKey, index: messageIndex, field: fieldId, value: newValue },
      {
        successTitle: newValue === null ? `Removed ${fieldId}` : `Updated ${fieldId}`,
        warningsAreNotes: true,
        onError: (message) => {
          failure = message
        },
      },
    )
    if (warnings == null) {
      setPending(false)
      setError(
        failure != null ? stripEditFieldErrorPrefix(failure) : 'The edit could not be applied.',
      )
      return
    }
    onClose()
  }

  const handleSubmit = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (pending) return
    const parsed = parseFieldDraft(description, draft, timeMode)
    if (!parsed.ok) {
      setError(parsed.error)
      return
    }
    void commit(parsed.value)
  }

  const hint = buildHint(description, draft, context, timeMode, unitSystem)
  const describedBy = [hint != null ? hintId : null, error != null ? errorId : null]
    .filter((id): id is string => id != null)
    .join(' ')
  const inputA11y = {
    'aria-invalid': error != null,
    'aria-describedby': describedBy.length > 0 ? describedBy : undefined,
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !pending) onClose()
      }}
    >
      <DialogContent aria-describedby={descriptionId}>
        <DialogTitle>{isSet ? 'Edit field' : 'Set field'}</DialogTitle>
        <DialogDescription id={descriptionId}>
          <span className="font-mono">{fieldId}</span> of {instanceLabel}
          {description.subFieldOf != null && (
            <>
              {' '}
              (an interpretation of <span className="font-mono">{description.subFieldOf}</span>)
            </>
          )}
          . Not recalculated from the records: the value is stored exactly as entered.
        </DialogDescription>

        <form noValidate onSubmit={handleSubmit} className="flex flex-col gap-3">
          <p className="text-xs">
            <span className="text-muted-foreground">Current value: </span>
            <span className="font-medium tabular-nums">
              {isSet ? formatCurrent(context, timeMode) : 'not set'}
            </span>
          </p>

          <div className="flex flex-col gap-1">
            <label htmlFor={inputId} className="text-foreground text-xs font-medium">
              New value
              {units != null && description.kind === 'number' && !isBool && ` (${units})`}
              {description.kind === 'date' && (timeMode === 'utc' ? ' (UTC)' : ' (local time)')}
            </label>

            {description.kind === 'number' && isBool && (
              <select
                id={inputId}
                value={draft.text}
                onChange={(e) => update({ text: e.target.value })}
                className={INPUT_CLASS}
                {...inputA11y}
              >
                {draft.text === '' && <option value="">Choose…</option>}
                <option value="0">0 (false)</option>
                <option value="1">1 (true)</option>
              </select>
            )}

            {description.kind === 'number' && !isBool && (
              <div className="flex items-center gap-2">
                <input
                  id={inputId}
                  type="number"
                  inputMode="decimal"
                  value={draft.text}
                  onChange={(e) => update({ text: e.target.value })}
                  min={description.min}
                  max={description.max}
                  step={description.resolution ?? 'any'}
                  className={INPUT_CLASS}
                  {...inputA11y}
                />
                {units != null && (
                  <span className="text-muted-foreground shrink-0 text-xs">{units}</span>
                )}
              </div>
            )}

            {description.kind === 'enum' && (
              <>
                <select
                  id={inputId}
                  value={draft.enumChoice}
                  onChange={(e) => update({ enumChoice: e.target.value })}
                  className={INPUT_CLASS}
                  {...(draft.enumChoice === RAW_ENUM_CHOICE ? {} : inputA11y)}
                >
                  {draft.enumChoice === '' && <option value="">Choose…</option>}
                  {(description.enumValues ?? []).map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                  <option value={RAW_ENUM_CHOICE}>Other (raw number)…</option>
                </select>
                {draft.enumChoice === RAW_ENUM_CHOICE && (
                  <>
                    <label htmlFor={rawInputId} className="text-foreground text-xs font-medium">
                      Raw value ({description.min ?? 0}–{description.max ?? 0})
                    </label>
                    <input
                      id={rawInputId}
                      type="number"
                      inputMode="numeric"
                      step={1}
                      min={description.min}
                      max={description.max}
                      value={draft.text}
                      onChange={(e) => update({ text: e.target.value })}
                      className={INPUT_CLASS}
                      {...inputA11y}
                    />
                  </>
                )}
              </>
            )}

            {description.kind === 'string' && (
              <input
                id={inputId}
                type="text"
                value={draft.text}
                onChange={(e) => update({ text: e.target.value })}
                className={INPUT_CLASS}
                {...inputA11y}
              />
            )}

            {description.kind === 'date' && (
              <input
                id={inputId}
                type="datetime-local"
                step={1}
                value={draft.text}
                onChange={(e) => update({ text: e.target.value })}
                min={
                  description.min != null
                    ? dateToInputValue(new Date(description.min), timeMode)
                    : undefined
                }
                max={
                  description.max != null
                    ? dateToInputValue(new Date(description.max), timeMode)
                    : undefined
                }
                className={INPUT_CLASS}
                {...inputA11y}
              />
            )}

            {hint != null && (
              <p id={hintId} className="text-muted-foreground text-xs">
                {hint}
              </p>
            )}
            {error != null && (
              <p id={errorId} role="alert" className="text-destructive text-xs">
                {error}
              </p>
            )}
          </div>

          <DialogFooter className="flex-wrap">
            {isSet && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="text-destructive mr-auto"
                disabled={pending}
                onClick={() => void commit(null)}
              >
                Remove field
              </Button>
            )}
            <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={pending}>
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={pending}>
              {pending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function formatCurrent(context: FieldCellContext, timeMode: 'utc' | 'local'): string {
  const decoded = formatDecodedValue(context.value, timeMode)
  if (decoded === EMPTY_VALUE) return decoded
  return typeof context.value === 'number' && context.units !== ''
    ? `${decoded} ${context.units}`
    : decoded
}

/** Range / byte count / converted-value hint under the input, or `null`. */
function buildHint(
  description: EditableFieldDescription,
  draft: FieldEditDraft,
  context: FieldCellContext,
  timeMode: 'utc' | 'local',
  unitSystem: ReturnType<typeof useUnits>,
): string | null {
  switch (description.kind) {
    case 'string':
      return description.maxBytes != null
        ? `${utf8ByteLength(draft.text)} / ${description.maxBytes} bytes (UTF-8)`
        : null
    case 'date':
      return 'Whole seconds. Interpreted in the time display mode (Local time / UTC) chosen on the dashboard.'
    case 'enum':
      return null
    case 'number': {
      if (context.meta?.type === 'bool') return null
      const parts: string[] = []
      const n = draft.text.trim() === '' ? Number.NaN : Number(draft.text)
      const units = description.units ?? ''
      if (Number.isFinite(n) && FRIENDLY_HINT_UNITS.has(units)) {
        parts.push(`= ${formatFriendlyValue(context.fieldId, n, units, timeMode, { unitSystem })}`)
      }
      if (
        description.resolution != null &&
        description.integer !== true &&
        description.resolution !== 1
      ) {
        const step = Number(description.resolution.toPrecision(6))
        parts.push(
          `Stored in steps of ${step}${units ? ` ${units}` : ''}; other values are rounded.`,
        )
      }
      if (description.integer === true) parts.push('Whole numbers only.')
      return parts.length > 0 ? parts.join(' ') : null
    }
  }
}
